import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import {
  addEpicChildren,
  getEpic,
  getLinkableEpicItems,
  getProjectEpics,
  markEpicDone,
  reopenEpic,
  setEpic,
} from '../api/epic';
import { ApiError } from 'types/apiError';
import {
  EpicChildrenPayload,
  EpicStatusFilter,
  EpicStatusPayload,
  EpicSummary,
  SetEpicPayload,
} from 'types/project/epic';

/**
 * Under the 'tasks' prefix, like the backlog: a child created, moved or done
 * changes an Epic's counts, and every work item change invalidates ['tasks'].
 */
export const projectEpicsQueryKey = (projectUuid: string) => ['tasks', 'epics', projectUuid] as const;

export const epicQueryKey = (epicUuid: string) => ['tasks', 'epic', epicUuid] as const;

export const useProjectEpicsQuery = (projectUuid?: string, status: EpicStatusFilter = 'ALL') =>
  useQuery({
    queryKey: [...projectEpicsQueryKey(projectUuid ?? ''), status] as const,
    queryFn: () => getProjectEpics(projectUuid as string, status),
    enabled: !!projectUuid,
    // Switching the Open / Done / All filter keeps the table and its counts in place.
    placeholderData: keepPreviousData,
  });

export const useEpicQuery = (projectUuid?: string, epicUuid?: string | null, hideDone = true) =>
  useQuery({
    queryKey: [...epicQueryKey(epicUuid ?? ''), { hideDone }] as const,
    queryFn: () => getEpic(projectUuid as string, epicUuid as string, hideDone),
    enabled: !!projectUuid && !!epicUuid,
    placeholderData: keepPreviousData,
  });

export const useLinkableEpicItemsQuery = (
  projectUuid: string,
  epicUuid: string,
  search: string,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ['tasks', 'epic-linkable', epicUuid, search] as const,
    queryFn: () => getLinkableEpicItems(projectUuid, epicUuid, search),
    enabled: enabled && !!projectUuid && !!epicUuid,
    placeholderData: keepPreviousData,
  });

/** The Epic's own work item page reads its status too, so it is refreshed with the lists. */
const invalidateEpic = (
  queryClient: ReturnType<typeof useQueryClient>,
  { projectUuid, epicUuid }: EpicStatusPayload,
) => {
  queryClient.invalidateQueries({ queryKey: ['tasks'] });
  queryClient.invalidateQueries({ queryKey: ['task', projectUuid, epicUuid] });
  queryClient.invalidateQueries({ queryKey: ['task-history', projectUuid, epicUuid] });
};

export const useAddEpicChildrenMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<EpicSummary, ApiError, EpicChildrenPayload>({
    mutationFn: addEpicChildren,
    onSuccess: (_epic, variables) => {
      invalidateEpic(queryClient, variables);
      const count = variables.workItemUuids.length;
      toast.success(`${count} work item${count === 1 ? '' : 's'} added to the Epic`);
    },
    onError: (error) => {
      toast.error(error?.message || "Couldn't add the work items to the Epic");
    },
  });
};

/** Row menu and bulk Set / Change / Remove parent Epic. */
export const useSetEpicMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<number, ApiError, SetEpicPayload>({
    mutationFn: setEpic,
    onSuccess: (_count, variables) => {
      // Every list, the Epics and each item's page show the Epic.
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task', variables.projectUuid] });
      const count = variables.workItemUuids.length;
      const items = `${count} work item${count === 1 ? '' : 's'}`;
      toast.success(variables.epicUuid ? `Epic set on ${items}` : `${items} removed from their Epic`);
    },
    onError: (error) => {
      toast.error(error?.message || "Couldn't change the Epic");
    },
  });
};

export const useMarkEpicDoneMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<EpicSummary, ApiError, EpicStatusPayload>({
    mutationFn: markEpicDone,
    onSuccess: (_epic, variables) => {
      invalidateEpic(queryClient, variables);
      toast.success('Epic marked as Done');
    },
    onError: (error) => {
      toast.error(error?.message || "Couldn't mark the Epic as Done");
    },
  });
};

export const useReopenEpicMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<EpicSummary, ApiError, EpicStatusPayload>({
    mutationFn: reopenEpic,
    onSuccess: (_epic, variables) => {
      invalidateEpic(queryClient, variables);
      toast.success('Epic reopened');
    },
    onError: (error) => {
      toast.error(error?.message || "Couldn't reopen the Epic");
    },
  });
};
